import { useState } from 'react';
import { supportBoardAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import AssigneeTags, { assigneeIdsOf } from './AssigneeTags';

function apiErrorMessage(error) {
  return error.response?.data?.detail || error.message || '저장하지 못했습니다.';
}

export default function ProjectModal({ project, statusLabels, assignees, onClose, onSaved, onDelete = null }) {
  const editing = Boolean(project);
  const [name, setName] = useState(project?.name || '');
  const [status, setStatus] = useState(project?.status || 'planned');
  const [description, setDescription] = useState(project?.description || '');
  const [assigneeIds, setAssigneeIds] = useState(() => assigneeIdsOf(project));
  const [goalTitle, setGoalTitle] = useState(project?.goal?.title || '');
  const [goalStartDate, setGoalStartDate] = useState(project?.goal?.start_date || '');
  const [goalEndDate, setGoalEndDate] = useState(project?.goal?.end_date || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  // 저장·삭제 중에는 닫기와 다른 버튼을 막는다 — 요청 두 개가 겹치지 않게.
  const busy = saving || deleting;

  // 목표가 있던 프로젝트는 수정에서도 목표를 비울 수 없다(카드의 '대표 목표일'이 빈다).
  const goalRequired = !editing || Boolean(project?.goal);

  const submit = async (event) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedGoalTitle = goalTitle.trim();
    if (!trimmedName) {
      setError('프로젝트명을 입력하세요.');
      return;
    }
    const goalTouched = goalRequired || Boolean(trimmedGoalTitle) || Boolean(goalEndDate);
    if (goalTouched && (!trimmedGoalTitle || !goalEndDate)) {
      setError('최종 목표명과 목표 완료일을 입력하세요.');
      return;
    }
    if (goalStartDate && goalEndDate && goalStartDate > goalEndDate) {
      setError('목표 시작일은 목표 완료일보다 늦을 수 없습니다.');
      return;
    }
    if (editing && project.status !== 'done' && status === 'done') {
      const confirmed = await confirmDialog('이 프로젝트를 완료 상태로 변경할까요?');
      if (!confirmed) return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        name: trimmedName,
        status,
        description,
        assignee_user_ids: assigneeIds,
      };
      if (editing) {
        // 🔴 바뀐 목표 항목만 보낸다 — 안 건드린 항목까지 덮어쓰면 그 사이 남이 고친 값이 날아간다.
        const goal = project?.goal || null;
        if (trimmedGoalTitle !== (goal?.title || '')) payload.goal_title = trimmedGoalTitle;
        if (goalStartDate !== (goal?.start_date || '')) payload.goal_start_date = goalStartDate || null;
        if (goalEndDate !== (goal?.end_date || '')) payload.goal_end_date = goalEndDate;
      } else {
        payload.goal_title = trimmedGoalTitle;
        payload.goal_end_date = goalEndDate;
        // 비워 두면 서버가 오늘로 채운다(종전 동작 유지).
        if (goalStartDate) payload.goal_start_date = goalStartDate;
      }
      const response = editing
        ? await supportBoardAPI.update(project.id, payload)
        : await supportBoardAPI.create(payload);
      onSaved(response.data);
    } catch (saveError) {
      setError(apiErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  // 확인을 받은 뒤에만 부른다. 실제 삭제(remove + 목록 재조회)와 성공 시 닫기는 페이지의 onDelete 가 한다.
  const remove = async () => {
    if (!editing || !onDelete || busy) return;
    if (!(await confirmDialog('삭제하시겠습니까?', { danger: true }))) return;
    setDeleting(true);
    setError('');
    try {
      await onDelete(project);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(17,24,39,.45)',
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-project-modal-title"
        onSubmit={submit}
        style={{
          width: 'min(520px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: '24px',
          borderRadius: RADIUS.lg,
          background: COLORS.surface,
          boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 id="support-project-modal-title" style={{ margin: '0 0 20px', color: COLORS.text, fontSize: '19px' }}>
          {editing ? '프로젝트 수정' : '프로젝트 추가'}
        </h2>

        <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
          프로젝트명
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            required
            style={{ ...inputStyle, marginTop: '6px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
          상태
          <select value={status} onChange={(event) => setStatus(event.target.value)} style={{ ...inputStyle, marginTop: '6px' }}>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
          설명
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            style={{ ...inputStyle, marginTop: '6px', resize: 'vertical' }}
          />
        </label>

        <div style={{ marginBottom: '14px' }}>
          <div style={{ marginBottom: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>담당자</div>
          <AssigneeTags
            ids={assigneeIds}
            people={assignees}
            onChange={setAssigneeIds}
            groupLabel="담당자"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(130px, .5fr) minmax(130px, .5fr)', gap: '10px' }}>
          <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            최종 목표명
            <input
              value={goalTitle}
              onChange={(event) => setGoalTitle(event.target.value)}
              maxLength={200}
              required={goalRequired}
              style={{ ...inputStyle, marginTop: '6px' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            목표 시작일
            <input
              type="date"
              value={goalStartDate}
              onChange={(event) => setGoalStartDate(event.target.value)}
              style={{ ...inputStyle, marginTop: '6px' }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '14px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            목표 완료일
            <input
              type="date"
              value={goalEndDate}
              onChange={(event) => setGoalEndDate(event.target.value)}
              required={goalRequired}
              style={{ ...inputStyle, marginTop: '6px' }}
            />
          </label>
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: '14px', color: COLORS.dangerText, fontSize: '13px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onClose} disabled={busy} style={btn('secondary')}>취소</button>
          <button type="submit" disabled={busy} style={{ ...btn('primary'), opacity: busy ? 0.65 : 1 }}>
            {saving ? '저장 중…' : '저장'}
          </button>
          {/* 삭제는 수정 팝업에만 있다(추가 팝업·onDelete 미지정이면 그리지 않는다). */}
          {editing && onDelete && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              style={{ ...btn('danger'), background: COLORS.danger, color: '#fff', opacity: busy ? 0.65 : 1 }}
            >
              {deleting ? '삭제 중…' : '삭제'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
